<!--
  ChatTranscript — renders the chat timeline. Replaces the monolith's giant
  v-if/v-else-if chain: each message is delegated to the right primitive / step /
  auth card by `kind`, inside a <transition-group> for the fade-up animation. Pure
  renderer — it emits semantic events up to SignupFlow, which routes them to the
  orchestrator's handlers.
-->
<template>
  <transition-group name="msg" tag="div" class="messages-inner">
    <template v-for="msg in messages" :key="msg.id">
      <TypingIndicator v-if="msg.kind === 'typing'" class="msg-item" :label="`${characterName} écrit…`" />

      <BotBubble
        v-else-if="msg.kind === 'bot'"
        class="msg-item"
        :content="(msg.content as string)"
        :show-avatar="(msg.showAvatar as boolean)"
        :avatar-src="avatarSrc"
        :alt="characterName"
      />

      <UserBubble
        v-else-if="msg.kind === 'user'"
        class="msg-item"
        :content="(msg.content as string)"
        :editable="(msg.editable as boolean)"
        :icon="(msg.icon as string | null)"
        @edit="$emit('edit', msg.step as string)"
      />

      <div v-else-if="msg.kind === 'choice'" class="msg-item inline-sel-row">
        <ChatChoiceCard
          :options="(msg.options as ChoiceOption[])"
          :selected-key="(msg.selectedKey as string | null)"
          @select="(key) => $emit('select', msg.stepKey as string, key)"
        />
      </div>

      <div v-else-if="msg.kind === 'address'" class="msg-item inline-sel-row">
        <AddressStep
          :cta-label="(msg.ctaLabel as string)"
          :initial-label="(msg.initialLabel as string | null)"
          :initial-city="(msg.initialCity as string | null)"
          :initial-geo="(msg.initialGeo as AddressGeo | null)"
          @confirm="$emit('confirmAddress', $event)"
        />
      </div>

      <div v-else-if="msg.kind === 'caretype'" class="msg-item inline-sel-row">
        <CareTypeStep :selected-key="(msg.selectedKey as string | null)" @select="$emit('careType', $event)" />
      </div>

      <div v-else-if="msg.kind === 'longform'" class="msg-item inline-sel-row">
        <LongFormCard
          :type="(msg.formType as 'mam' | 'rpe' | 'trainer' | 'agency')"
          :disabled="submitInFlight"
          @submit="$emit('longformSubmit', $event)"
        />
      </div>

      <div v-else-if="msg.kind === 'auth-choice'" class="msg-item inline-sel-row">
        <AuthChoiceCard
          :disabled="submitInFlight"
          :error-message="(msg.errorMessage as string | null)"
          @choose-google="$emit('authGoogle')"
          @choose-email="$emit('authEmail')"
          @already-have-account="$emit('alreadyHaveAccount')"
        />
      </div>

      <div v-else-if="msg.kind === 'cgua-consent'" class="msg-item inline-sel-row">
        <CguaConsentCard :disabled="submitInFlight" @accept="$emit('acceptCgua')" />
      </div>

      <div v-else-if="msg.kind === 'email-form'" class="msg-item inline-sel-row">
        <EmailFormCard
          :disabled="submitInFlight"
          :error-message="emailError"
          @submit="$emit('emailSubmit', $event)"
          @already-have-account="$emit('alreadyHaveAccount')"
        />
      </div>

    </template>
  </transition-group>
</template>

<script setup lang="ts">
import type { ChatMessage } from '../composables/useChatTimeline'
import type { ChoiceOption } from '../config/options'
import type { AddressGeo, AddressSuggestion } from '../composables/useAddressAutocomplete'
import type { LongFormPayload } from './steps/LongFormCard.vue'
import { BotBubble, UserBubble, TypingIndicator, ChatChoiceCard } from '@twg/ui'
import AddressStep from './steps/AddressStep.vue'
import CareTypeStep from './steps/CareTypeStep.vue'
import LongFormCard from './steps/LongFormCard.vue'
import AuthChoiceCard from './auth/AuthChoiceCard.vue'
import EmailFormCard from './auth/EmailFormCard.vue'
import CguaConsentCard from './auth/CguaConsentCard.vue'

defineProps<{
  messages: ChatMessage[]
  avatarSrc: string
  characterName: string
  submitInFlight: boolean
  emailError: string | null
}>()

defineEmits<{
  edit: [step: string]
  select: [stepKey: string, key: string]
  careType: [key: string]
  confirmAddress: [address: AddressSuggestion]
  longformSubmit: [payload: LongFormPayload]
  authGoogle: []
  authEmail: []
  alreadyHaveAccount: []
  emailSubmit: [credentials: { email: string; password: string }]
  acceptCgua: []
}>()
</script>

<style scoped>
.messages-inner {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 24px;
}
/* Vertical rhythm: 8px between same-speaker rows, 16px across turns, 24px before a card. */
.msg-item + .msg-item {
  margin-top: 8px;
}
.msg-item + .inline-sel-row {
  margin-top: 24px;
}
.inline-sel-row {
  padding: 8px 0;
}
.msg-enter-active {
  transition: opacity 0.4s cubic-bezier(0.2, 0.6, 0.3, 1), transform 0.4s cubic-bezier(0.2, 0.6, 0.3, 1);
}
.msg-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
@media (prefers-reduced-motion: reduce) {
  .msg-enter-active {
    transition: none;
  }
  .msg-enter-from {
    opacity: 1;
    transform: none;
  }
}
</style>
